import React, { useState } from "react";
import PageInstructions from "@/components/PageInstructions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import jsPDF from 'jspdf';

interface SequenceDetail {
  grupo: number;
  premio: string;
}

interface SequenceAnalysis {
  [key: number]: SequenceDetail[];
}

const MetodoSequencia = () => {
  const [premios, setPremios] = useState({
    primeiro: "",
    segundo: "",
    terceiro: "",
    quarto: "",
    quinto: "",
    sexto: ""
  });
  const [showResults, setShowResults] = useState(false);
  const [contadorSequencias, setContadorSequencias] = useState<{[key: number]: number}>({});
  const [detalhesSequencias, setDetalhesSequencias] = useState<SequenceAnalysis>({});
  const [sequenciasDestaque, setSequenciasDestaque] = useState<number[]>([]);
  const [maxOcorrencias, setMaxOcorrencias] = useState(0);

  const sequencias = {
    1: [1, 6, 11, 16, 21],
    2: [2, 7, 12, 17, 22],
    3: [3, 8, 13, 18, 23],
    4: [4, 9, 14, 19, 24],
    5: [5, 10, 15, 20, 25]
  };

  const getGrupo = (numero: string): number => {
    return parseInt(numero.toString().slice(-2));
  };

  const getSequencia = (grupo: number): number | null => {
    for (let seq = 1; seq <= 5; seq++) {
      if (sequencias[seq as keyof typeof sequencias].includes(grupo)) {
        return seq;
      }
    }
    return null;
  };

  const handleInputChange = (field: string, value: string) => {
    setPremios(prev => ({ ...prev, [field]: value }));
    setShowResults(false);
  };

  const analisarSequencia = () => {
    const { primeiro, segundo, terceiro, quarto, quinto, sexto } = premios;

    if (!primeiro || !segundo || !terceiro || !quarto || !quinto) {
      alert('Por favor, preencha pelo menos os 5 primeiros prêmios!');
      return;
    }

    // Extrair grupos
    const grupos = {
      primeiro: getGrupo(primeiro),
      segundo: getGrupo(segundo),
      terceiro: getGrupo(terceiro),
      quarto: getGrupo(quarto),
      quinto: getGrupo(quinto),
      sexto: sexto ? getGrupo(sexto) : null
    };

    // Analisar apenas os prêmios "por dentro" (2º ao 5º, e 6º se existir)
    const gruposPorDentro = [grupos.segundo, grupos.terceiro, grupos.quarto, grupos.quinto];
    if (grupos.sexto) {
      gruposPorDentro.push(grupos.sexto);
    }
    
    // Contar ocorrências por sequência
    const contador: {[key: number]: number} = {1: 0, 2: 0, 3: 0, 4: 0, 5: 0};
    const detalhes: SequenceAnalysis = {1: [], 2: [], 3: [], 4: [], 5: []};

    gruposPorDentro.forEach((grupo, index) => {
      const sequencia = getSequencia(grupo);
      if (sequencia) {
        contador[sequencia]++;
        let premioLabel;
        if (index === 0) premioLabel = '2º';
        else if (index === 1) premioLabel = '3º';
        else if (index === 2) premioLabel = '4º';
        else if (index === 3) premioLabel = '5º';
        else if (index === 4) premioLabel = '6º';
        
        detalhes[sequencia].push({
          grupo: grupo,
          premio: premioLabel!
        });
      }
    });

    // Fazer previsão
    const maxOcor = Math.max(...Object.values(contador));
    const destaque: number[] = [];

    for (let seq = 1; seq <= 5; seq++) {
      if (contador[seq] === maxOcor && maxOcor >= 2) {
        destaque.push(seq);
      }
    }

    setContadorSequencias(contador);
    setDetalhesSequencias(detalhes);
    setSequenciasDestaque(destaque);
    setMaxOcorrencias(maxOcor);
    setShowResults(true);
  };

  const exportData = () => {
    if (!showResults) {
      alert('⚠️ Primeiro analise as sequências para poder exportar!');
      return;
    }

    const doc = new jsPDF();
    const currentDate = new Date().toLocaleString('pt-BR');
    
    // Header
    doc.setFontSize(20);
    doc.text('Método da Sequência - Relatório', 20, 20);
    
    doc.setFontSize(12);
    doc.text(`Data de Exportação: ${currentDate}`, 20, 35);
    
    let yPos = 55;
    
    // Prêmios inseridos
    doc.setFontSize(14);
    doc.text('Prêmios Analisados:', 20, yPos);
    yPos += 15;
    
    doc.setFontSize(10);
    doc.text(`1º Prêmio (Cabeça): ${premios.primeiro}`, 20, yPos);
    yPos += 10;
    doc.text(`2º Prêmio: ${premios.segundo} (Grupo: ${getGrupo(premios.segundo)})`, 20, yPos);
    yPos += 10;
    doc.text(`3º Prêmio: ${premios.terceiro} (Grupo: ${getGrupo(premios.terceiro)})`, 20, yPos);
    yPos += 10;
    doc.text(`4º Prêmio: ${premios.quarto} (Grupo: ${getGrupo(premios.quarto)})`, 20, yPos);
    yPos += 10;
    doc.text(`5º Prêmio: ${premios.quinto} (Grupo: ${getGrupo(premios.quinto)})`, 20, yPos);
    yPos += 10;
    if (premios.sexto) {
      doc.text(`6º Prêmio: ${premios.sexto} (Grupo: ${getGrupo(premios.sexto)})`, 20, yPos);
      yPos += 10;
    }
    
    yPos += 20;
    
    // Análise das sequências
    doc.setFontSize(14);
    doc.text('Análise das Sequências:', 20, yPos);
    yPos += 15;
    
    Object.entries(contadorSequencias).forEach(([seq, count]) => {
      doc.setFontSize(10);
      doc.text(`${seq}ª Sequência: ${count} ocorrência(s)`, 20, yPos);
      yPos += 10;
    });
    
    yPos += 20;
    
    // Sequências em destaque
    if (sequenciasDestaque.length > 0) {
      doc.setFontSize(14);
      doc.text('Sequências com Maior Potencial:', 20, yPos);
      yPos += 15;
      
      doc.setFontSize(10);
      sequenciasDestaque.forEach(seq => {
        doc.text(`${seq}ª Sequência: ${sequencias[seq as keyof typeof sequencias].join(', ')}`, 20, yPos);
        yPos += 10;
      });
    }
    
    doc.save(`metodo_sequencia_${new Date().toISOString().split('T')[0]}.pdf`);
    alert('📤 PDF exportado com sucesso!');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      analisarSequencia();
    }
  };

  return (
    <div className="w-full space-y-6">
      <PageInstructions
        title="📖 Como usar o Método da Sequência"
        steps={[
          "Digite o número do resultado do último sorteio",
          "Clique em 'Calcular Sequência' para gerar os palpites",
          "O sistema analisa o número e encontra sequências matemáticas",
          "Veja as centenas, dezenas e grupos sugeridos",
          "Exporte o resultado em PDF para levar suas apostas prontas"
        ]}
      />
      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-cyan-500 to-blue-600 bg-clip-text text-transparent">
          Método da Sequência
        </h1>
        <p className="text-xl text-muted-foreground">Análise de Sequências para Loteria</p>
      </div>

      {/* Description */}
      <Card className="border-l-4 border-l-cyan-500 bg-gradient-to-r from-slate-50 to-gray-50">
        <CardContent className="p-6">
          <p className="text-muted-foreground leading-relaxed">
            <strong className="text-slate-700">Como funciona:</strong> O Método da Sequência analisa os grupos dos prêmios "por dentro" (2º ao 5º, e 6º quando disponível) para identificar padrões. Quando três ou mais grupos de uma mesma sequência aparecem, isso indica que essa sequência pode "subir" para o 1º prêmio no próximo sorteio.
          </p>
        </CardContent>
      </Card>

      {/* Sequences Display */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {Object.entries(sequencias).map(([seqNum, grupos]) => (
          <Card key={seqNum} className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white border-0">
            <CardContent className="p-4 text-center">
              <h3 className="font-bold mb-3 text-lg">{seqNum}ª Sequência</h3>
              <div className="flex flex-wrap justify-center gap-2">
                {grupos.map((grupo) => (
                  <span key={grupo} className="bg-white/20 px-3 py-1 rounded-full text-sm font-bold">
                    {grupo.toString().padStart(2, '0')}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Input Section */}
      <Card className="border-2 border-cyan-200 bg-gradient-to-br from-cyan-50 to-blue-50">
        <CardHeader>
          <CardTitle className="text-cyan-600 text-xl text-center">📊 Insira os Resultados do Último Sorteio</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="font-semibold text-slate-700">🥇 1º Prêmio (Cabeça):</label>
              <input
                type="number"
                placeholder="Ex: 1425"
                min="1"
                max="9999"
                value={premios.primeiro}
                onChange={(e) => handleInputChange('primeiro', e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full p-3 border-2 border-cyan-300 rounded-lg text-center font-bold text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-500 placeholder:text-gray-500"
              />
            </div>
            <div className="space-y-2">
              <label className="font-semibold text-slate-700">🥈 2º Prêmio:</label>
              <input
                type="number"
                placeholder="Ex: 3687"
                min="1"
                max="9999"
                value={premios.segundo}
                onChange={(e) => handleInputChange('segundo', e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full p-3 border-2 border-cyan-300 rounded-lg text-center font-bold text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-500 placeholder:text-gray-500"
              />
            </div>
            <div className="space-y-2">
              <label className="font-semibold text-slate-700">🥉 3º Prêmio:</label>
              <input
                type="number"
                placeholder="Ex: 8921"
                min="1"
                max="9999"
                value={premios.terceiro}
                onChange={(e) => handleInputChange('terceiro', e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full p-3 border-2 border-cyan-300 rounded-lg text-center font-bold text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-500 placeholder:text-gray-500"
              />
            </div>
            <div className="space-y-2">
              <label className="font-semibold text-slate-700">4️⃣ 4º Prêmio:</label>
              <input
                type="number"
                placeholder="Ex: 0456"
                min="1"
                max="9999"
                value={premios.quarto}
                onChange={(e) => handleInputChange('quarto', e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full p-3 border-2 border-cyan-300 rounded-lg text-center font-bold text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-500 placeholder:text-gray-500"
              />
            </div>
            <div className="space-y-2">
              <label className="font-semibold text-slate-700">5️⃣ 5º Prêmio:</label>
              <input
                type="number"
                placeholder="Ex: 7234"
                min="1"
                max="9999"
                value={premios.quinto}
                onChange={(e) => handleInputChange('quinto', e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full p-3 border-2 border-cyan-300 rounded-lg text-center font-bold text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-500 placeholder:text-gray-500"
              />
            </div>
            <div className="space-y-2">
              <label className="font-semibold text-slate-700">6️⃣ 6º Prêmio (opcional):</label>
              <input
                type="number"
                placeholder="Ex: 9876"
                min="1"
                max="9999"
                value={premios.sexto}
                onChange={(e) => handleInputChange('sexto', e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full p-3 border-2 border-cyan-300 rounded-lg text-center font-bold text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-cyan-500 placeholder:text-gray-500"
              />
              <small className="text-muted-foreground text-sm block">
                *Algumas loterias incluem o 6º prêmio
              </small>
            </div>
          </div>

          <Button
            onClick={analisarSequencia}
            className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white py-4 text-lg font-bold rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-lg"
          >
            🔍 Analisar Sequências
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {showResults && (
        <Card className="animate-in slide-in-from-bottom-4 duration-500">
          <CardContent className="p-0">
            <div className="bg-gradient-to-r from-orange-400 to-pink-500 text-white p-6 rounded-t-lg">
              <h3 className="text-2xl font-bold text-center mb-6">📈 Análise das Sequências</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                {Object.entries(contadorSequencias).map(([seq, count]) => (
                  <div
                    key={seq}
                    className={`bg-white/15 backdrop-blur-sm p-4 rounded-xl ${
                      count >= 2 ? 'border-2 border-white/80 bg-white/30' : ''
                    }`}
                  >
                    <div className="font-bold mb-2">{seq}ª Sequência</div>
                    <div className="text-xl mb-2">{count} ocorrência(s)</div>
                    <div className="text-sm opacity-90">
                      {detalhesSequencias[parseInt(seq)]?.length > 0 ? 
                        detalhesSequencias[parseInt(seq)].map(d => `${d.premio}: ${d.grupo.toString().padStart(2, '0')}`).join(', ') : 
                        'Nenhuma ocorrência'
                      }
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-white/20 backdrop-blur-sm p-5 rounded-xl">
                <h4 className="text-xl font-bold text-center mb-3">🎯 Sequência(s) com Maior Potencial</h4>
                
                {sequenciasDestaque.length > 0 && maxOcorrencias >= 2 ? (
                  <div className="space-y-4">
                    <div className="text-center">
                      <div className="text-lg">
                        <strong>Sequência(s) com maior potencial:</strong> {sequenciasDestaque.map(s => `${s}ª`).join(', ')}
                      </div>
                      <div className="text-sm opacity-90 mt-2">
                        Estas sequências tiveram {maxOcorrencias} ou mais ocorrências nos prêmios "por dentro" (2º ao 5º{premios.sexto ? ' ao 6º' : ''})
                      </div>
                    </div>
                    
                    <div className="flex justify-center gap-3 flex-wrap">
                      {sequenciasDestaque.map(seq => 
                        sequencias[seq as keyof typeof sequencias].map(grupo => (
                          <div
                            key={`${seq}-${grupo}`}
                            className="bg-white text-pink-500 px-4 py-2 rounded-full font-bold text-lg shadow-lg"
                          >
                            {grupo.toString().padStart(2, '0')}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="text-lg italic">
                      Nenhuma sequência teve 2 ou mais ocorrências nos prêmios "por dentro" (2º ao 5º{premios.sexto ? ' ao 6º' : ''}).
                    </div>
                    <div className="text-sm opacity-90 mt-2">
                      Analise mais sorteios para identificar padrões mais claros.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Export Button */}
      {showResults && (
        <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
          <CardContent className="p-6 text-center">
            <Button
              onClick={exportData}
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-8 py-3 text-lg font-bold rounded-xl"
            >
              📤 Exportar PDF
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MetodoSequencia;